/**
 * Centralized Data Client
 * Provides offline-first data access layer
 * Handles cache reads, network fetches, and cache synchronization
 */

import { isOnline } from '../apiRequests/offlineQueue';
import * as cache from './localCache';
import axiosWrapper from '../apiRequests/axiosWrapper';

/**
 * Normalize entity data - ensure it has required fields
 */
const normalizeEntity = (entity) => {
  if (!entity) return null;
  // Ensure id/_id exists
  if (!entity._id && !entity.id) {
    return null;
  }

  // Ensure updatedAt exists
  const normalized = {
    ...entity,
    updatedAt: entity.updatedAt || entity.createdAt || new Date().toISOString(),
  };

  return normalized;
};

/**
 * Normalize array of entities
 */
const normalizeEntities = (entities) => {
  if (!Array.isArray(entities)) return [];
  return entities.map((entity) => normalizeEntity(entity)).filter((e) => e !== null);
};

/**
 * Fetch projects from API and update cache
 */
const fetchAndCacheProjects = async () => {
  try {
    const response = await axiosWrapper({
      method: 'get',
      path: 'projects',
    });
    if (response.error) {
      return { data: null, fromCache: false, error: response.error };
    }

    const projects = response.data?.projects || response.data?.data || [];
    const normalized = normalizeEntities(projects);

    // Update cache
    if (normalized.length > 0) {
      await cache.setCachedItems('projects', normalized);
    }

    return { data: normalized, fromCache: false, error: null };
  } catch (error) {
    console.error('[dataClient] Error fetching projects:', error);
    return { data: null, fromCache: false, error };
  }
};

/**
 * Fetch single project from API and update cache
 */
const fetchAndCacheProject = async (projectId) => {
  try {
    const response = await axiosWrapper({
      method: 'get',
      path: `projects/${projectId}`,
    });
    if (response.error) {
      return { data: null, fromCache: false, error: response.error };
    }

    const project = response.data?.project || response.data?.data;
    const normalized = normalizeEntity(project);

    if (normalized) {
      await cache.setCachedItem('projects', normalized);
    }

    return { data: normalized, fromCache: false, error: null };
  } catch (error) {
    console.error('[dataClient] Error fetching project:', error);
    return { data: null, fromCache: false, error };
  }
};

/**
 * Get projects for current user
 */
export const getProjects = async (options = {}) => {
  const { useCache = true, backgroundRefresh = false } = options;

  // If offline or useCache is true, try cache first
  if (!isOnline() || useCache) {
    try {
      const cachedProjects = await cache.getCachedItems('projects');
      if (cachedProjects && cachedProjects.length > 0) {
        // If online and backgroundRefresh, fetch in background
        if (isOnline() && backgroundRefresh) {
          // Don't await - let it update cache in background
          fetchAndCacheProjects().catch((err) => {
            console.warn('[dataClient] Background refresh failed:', err);
          });
        }
        return { data: cachedProjects, fromCache: true, error: null };
      }
    } catch (error) {
      console.warn('[dataClient] Error reading projects from cache:', error);
    }
  }

  // If offline and no cache, return error
  if (!isOnline()) {
    return { data: null, fromCache: false, error: { message: 'Offline and no cached data' } };
  }

  // Fetch from network
  return fetchAndCacheProjects();
};

/**
 * Get a single project by ID
 */
export const getProject = async (projectId, options = {}) => {
  const { useCache = true, backgroundRefresh = false } = options;

  // Try cache first
  if (useCache) {
    try {
      const cachedProject = await cache.getCachedItem('projects', projectId);
      if (cachedProject) {
        // Background refresh if online
        if (isOnline() && backgroundRefresh) {
          fetchAndCacheProject(projectId).catch((err) => {
            console.warn('[dataClient] Background refresh failed:', err);
          });
        }
        return { data: cachedProject, fromCache: true, error: null };
      }
    } catch (error) {
      console.warn('[dataClient] Error reading project from cache:', error);
    }
  }

  // If offline, return error
  if (!isOnline()) {
    return { data: null, fromCache: false, error: { message: 'Offline and not cached' } };
  }

  // Fetch from network
  return fetchAndCacheProject(projectId);
};

/**
 * Get chapters for a project
 */
export const getChapters = async (projectId, options = {}) => {
  const { useCache = true } = options;

  // Try cache first
  if (useCache) {
    try {
      const cachedChapters = await cache.getCachedItems('chapters', {
        index: 'projectId',
        value: projectId,
      });
      if (cachedChapters && cachedChapters.length > 0) {
        return { data: cachedChapters, fromCache: true, error: null };
      }
    } catch (error) {
      console.warn('[dataClient] Error reading chapters from cache:', error);
    }
  }

  // If offline, return empty or error
  if (!isOnline()) {
    return { data: [], fromCache: false, error: { message: 'Offline and not cached' } };
  }

  // Note: There's no bulk fetch endpoint for chapters, so we'd need to fetch individually
  // For now, return empty array if not cached
  return { data: [], fromCache: false, error: null };
};

/**
 * Fetch single chapter from API and update cache
 */
const fetchAndCacheChapter = async (chapterId) => {
  try {
    const response = await axiosWrapper({
      method: 'get',
      path: `chapters/${chapterId}`,
    });
    if (response.error) {
      return { data: null, fromCache: false, error: response.error };
    }

    const chapter = response.data?.chapter || response.data?.data;
    const normalized = normalizeEntity(chapter);

    if (normalized) {
      await cache.setCachedItem('chapters', normalized);
    }

    return { data: normalized, fromCache: false, error: null };
  } catch (error) {
    console.error('[dataClient] Error fetching chapter:', error);
    return { data: null, fromCache: false, error };
  }
};

/**
 * Get a single chapter by ID
 */
export const getChapter = async (chapterId, options = {}) => {
  const { useCache = true, backgroundRefresh = false } = options;

  // Try cache first
  if (useCache) {
    try {
      const cachedChapter = await cache.getCachedItem('chapters', chapterId);
      if (cachedChapter) {
        // Background refresh if online
        if (isOnline() && backgroundRefresh) {
          fetchAndCacheChapter(chapterId).catch((err) => {
            console.warn('[dataClient] Background refresh failed:', err);
          });
        }
        return { data: cachedChapter, fromCache: true, error: null };
      }
    } catch (error) {
      console.warn('[dataClient] Error reading chapter from cache:', error);
    }
  }

  // If offline, return error
  if (!isOnline()) {
    return { data: null, fromCache: false, error: { message: 'Offline and not cached' } };
  }

  // Fetch from network
  return fetchAndCacheChapter(chapterId);
};

/**
 * Drop cached scenes for a chapter that no longer exist on the server, then upsert current rows.
 * (setCachedItems only puts — it never removes deleted scenes without this step.)
 */
const reconcileCachedScenesForChapter = async (chapterIdStr, normalizedServerScenes) => {
  const chapterKey = String(chapterIdStr);
  let cached = [];
  try {
    cached = await cache.getCachedItems('scenes', {
      index: 'chapterId',
      value: chapterKey,
    });
  } catch (e) {
    console.warn('[dataClient] Error reading scenes cache for reconcile:', e);
    return;
  }

  if (!normalizedServerScenes.length) {
    await Promise.all(cached.map((row) => cache.deleteCachedItem('scenes', row._id || row.id)));
    return;
  }

  const serverKeys = new Set();
  normalizedServerScenes.forEach((s) => {
    if (s._id) serverKeys.add(String(s._id));
    if (s.id) serverKeys.add(String(s.id));
  });

  const stale = cached.filter((row) => {
    const keys = [row._id && String(row._id), row.id && String(row.id)].filter(Boolean);
    return keys.length > 0 && !keys.some((k) => serverKeys.has(k));
  });

  await Promise.all(stale.map((row) => cache.deleteCachedItem('scenes', row._id || row.id)));
  await cache.setCachedItems('scenes', normalizedServerScenes);
};

/**
 * Get scenes for a chapter
 * When online, always loads from the server so IndexedDB cannot hide stale/deleted scenes.
 */
export const getScenes = async (chapterId, options = {}) => {
  const { useCache = true } = options;
  const chapterKey = String(chapterId);

  const loadCacheForChapter = async () => {
    try {
      const rows = await cache.getCachedItems('scenes', {
        index: 'chapterId',
        value: chapterKey,
      });
      return Array.isArray(rows) ? rows : [];
    } catch (error) {
      console.warn('[dataClient] Error reading scenes from cache:', error);
      return [];
    }
  };

  if (isOnline()) {
    try {
      const response = await axiosWrapper({
        method: 'get',
        path: `chapters/${chapterKey}/scenes`,
      });
      if (!response.error) {
        const scenes = response.data?.scenes || [];
        const normalized = normalizeEntities(scenes);
        await reconcileCachedScenesForChapter(chapterKey, normalized);
        return { data: normalized, fromCache: false, error: null };
      }
      if (useCache) {
        const cached = await loadCacheForChapter();
        if (cached.length > 0) {
          return { data: cached, fromCache: true, error: response.error };
        }
      }
      return { data: null, fromCache: false, error: response.error };
    } catch (error) {
      console.error('[dataClient] Error fetching scenes:', error);
      if (useCache) {
        const cached = await loadCacheForChapter();
        if (cached.length > 0) {
          return { data: cached, fromCache: true, error };
        }
      }
      return { data: null, fromCache: false, error };
    }
  }

  if (useCache) {
    const cached = await loadCacheForChapter();
    if (cached.length > 0) {
      return { data: cached, fromCache: true, error: null };
    }
  }

  return { data: [], fromCache: false, error: { message: 'Offline and not cached' } };
};

/**
 * Get notes for a project
 */
export const getNotes = async (projectId, options = {}) => {
  const { useCache = true, featureContext, parentType, parentId } = options;

  // Try cache first
  if (useCache) {
    try {
      let cachedNotes = await cache.getCachedItems('notes', {
        index: 'projectId',
        value: projectId,
      });

      // Filter by featureContext, parentType, parentId if provided
      if (featureContext) {
        cachedNotes = cachedNotes.filter((note) => note.featureContext === featureContext);
      }
      if (parentType) {
        cachedNotes = cachedNotes.filter((note) => note.parentType === parentType);
      }
      if (parentId) {
        cachedNotes = cachedNotes.filter((note) => note.parentId === parentId);
      }

      if (cachedNotes && cachedNotes.length > 0) {
        return { data: cachedNotes, fromCache: true, error: null };
      }
    } catch (error) {
      console.warn('[dataClient] Error reading notes from cache:', error);
    }
  }

  // If offline, return empty
  if (!isOnline()) {
    return { data: [], fromCache: false, error: { message: 'Offline and not cached' } };
  }

  // Fetch from network
  try {
    const queryParams = new URLSearchParams();
    if (featureContext) queryParams.append('featureContext', featureContext);
    if (parentType) queryParams.append('parentType', parentType);
    if (parentId) queryParams.append('parentId', parentId);
    const queryString = queryParams.toString();
    const path = `projects/${projectId}/notes${queryString ? `?${queryString}` : ''}`;

    const response = await axiosWrapper({
      method: 'get',
      path,
    });
    if (response.error) {
      return { data: null, fromCache: false, error: response.error };
    }

    const notes = response.data?.notes || response.data?.data || [];
    const normalized = normalizeEntities(notes);

    // Update cache
    if (normalized.length > 0) {
      await cache.setCachedItems('notes', normalized);
    }

    return { data: normalized, fromCache: false, error: null };
  } catch (error) {
    console.error('[dataClient] Error fetching notes:', error);
    return { data: null, fromCache: false, error };
  }
};

/**
 * Get corkboards for a project
 */
export const getCorkboards = async (projectId, options = {}) => {
  const { useCache = true } = options;

  // Try cache first
  if (useCache) {
    try {
      const cachedCorkboards = await cache.getCachedItems('corkboards', {
        index: 'projectId',
        value: projectId,
      });
      if (cachedCorkboards && cachedCorkboards.length > 0) {
        return { data: cachedCorkboards, fromCache: true, error: null };
      }
    } catch (error) {
      console.warn('[dataClient] Error reading corkboards from cache:', error);
    }
  }

  // If offline, return empty
  if (!isOnline()) {
    return { data: [], fromCache: false, error: { message: 'Offline and not cached' } };
  }

  // Fetch from network
  try {
    const response = await axiosWrapper({
      method: 'get',
      path: `projects/${projectId}/corkboards`,
    });
    if (response.error) {
      return { data: null, fromCache: false, error: response.error };
    }

    const corkboards = response.data?.corkboards || response.data?.data || [];
    const normalized = normalizeEntities(corkboards);

    // Update cache
    if (normalized.length > 0) {
      await cache.setCachedItems('corkboards', normalized);
    }

    return { data: normalized, fromCache: false, error: null };
  } catch (error) {
    console.error('[dataClient] Error fetching corkboards:', error);
    return { data: null, fromCache: false, error };
  }
};

/**
 * Get timelines for a project
 */
export const getTimelines = async (projectId, options = {}) => {
  const { useCache = true } = options;

  // Try cache first
  if (useCache) {
    try {
      const cachedTimelines = await cache.getCachedItems('timelines', {
        index: 'projectId',
        value: projectId,
      });
      if (cachedTimelines && cachedTimelines.length > 0) {
        return { data: cachedTimelines, fromCache: true, error: null };
      }
    } catch (error) {
      console.warn('[dataClient] Error reading timelines from cache:', error);
    }
  }

  // If offline, return empty
  if (!isOnline()) {
    return { data: [], fromCache: false, error: { message: 'Offline and not cached' } };
  }

  // Fetch from network
  try {
    const response = await axiosWrapper({
      method: 'get',
      path: `projects/${projectId}/timelines`,
    });
    if (response.error) {
      return { data: null, fromCache: false, error: response.error };
    }

    const timelines = response.data?.timelines || response.data?.data || [];
    const normalized = normalizeEntities(timelines);

    // Update cache
    if (normalized.length > 0) {
      await cache.setCachedItems('timelines', normalized);
    }

    return { data: normalized, fromCache: false, error: null };
  } catch (error) {
    console.error('[dataClient] Error fetching timelines:', error);
    return { data: null, fromCache: false, error };
  }
};

/**
 * Get characters for a project
 */
export const getCharacters = async (projectId, options = {}) => {
  const { useCache = true } = options;

  // Try cache first
  if (useCache) {
    try {
      const cachedCharacters = await cache.getCachedItems('characters', {
        index: 'projectId',
        value: projectId,
      });
      if (cachedCharacters && cachedCharacters.length > 0) {
        return { data: cachedCharacters, fromCache: true, error: null };
      }
    } catch (error) {
      console.warn('[dataClient] Error reading characters from cache:', error);
    }
  }

  // If offline, return empty
  if (!isOnline()) {
    return { data: [], fromCache: false, error: { message: 'Offline and not cached' } };
  }

  // Fetch from network
  try {
    const response = await axiosWrapper({
      method: 'get',
      path: `projects/${projectId}/characters`,
    });
    if (response.error) {
      return { data: null, fromCache: false, error: response.error };
    }

    const characters = response.data?.characters || response.data?.data || [];
    const normalized = normalizeEntities(characters);

    // Update cache
    if (normalized.length > 0) {
      await cache.setCachedItems('characters', normalized);
    }

    return { data: normalized, fromCache: false, error: null };
  } catch (error) {
    console.error('[dataClient] Error fetching characters:', error);
    return { data: null, fromCache: false, error };
  }
};

/**
 * Get families for a project
 */
export const getFamilies = async (projectId, options = {}) => {
  const { useCache = true } = options;

  // Try cache first
  if (useCache) {
    try {
      const cachedFamilies = await cache.getCachedItems('families', {
        index: 'projectId',
        value: projectId,
      });
      if (cachedFamilies && cachedFamilies.length > 0) {
        return { data: cachedFamilies, fromCache: true, error: null };
      }
    } catch (error) {
      console.warn('[dataClient] Error reading families from cache:', error);
    }
  }

  // If offline, return empty
  if (!isOnline()) {
    return { data: [], fromCache: false, error: { message: 'Offline and not cached' } };
  }

  // Fetch from network
  try {
    const response = await axiosWrapper({
      method: 'get',
      path: `projects/${projectId}/families`,
    });
    if (response.error) {
      return { data: null, fromCache: false, error: response.error };
    }

    const families = response.data?.families || response.data?.data || [];
    const normalized = normalizeEntities(families);

    // Update cache
    if (normalized.length > 0) {
      await cache.setCachedItems('families', normalized);
    }

    return { data: normalized, fromCache: false, error: null };
  } catch (error) {
    console.error('[dataClient] Error fetching families:', error);
    return { data: null, fromCache: false, error };
  }
};

/**
 * Update cache after mutation (create/update/delete)
 */
export const updateCacheAfterMutation = async (entityType, item, operation = 'update') => {
  try {
    if (operation === 'delete') {
      const key = item._id || item.id || item;
      await cache.deleteCachedItem(entityType, key);
    } else if (operation === 'create' || operation === 'update') {
      const normalized = normalizeEntity(item);
      if (normalized) {
        await cache.setCachedItem(entityType, normalized);
      }
    }
    return true;
  } catch (error) {
    console.error('[dataClient] Error updating cache after mutation:', error);
    return false;
  }
};

export default {
  getProjects,
  getProject,
  getChapters,
  getScenes,
  getNotes,
  getCorkboards,
  getTimelines,
  getCharacters,
  getFamilies,
  updateCacheAfterMutation,
};
